"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _axios = _interopRequireDefault(require("axios"));
const createInterceptorStack = function (interceptors) {
  return function (config) {
    return interceptors.reduce(function (promise, interceptor) {
      return promise.then(interceptor);
    }, Promise.resolve(config));
  };
};

/**
 * Creates a configurable Axios-based HTTP client with support for both request and response interceptors.
 *
 * @template Config - Extends Axios's `InternalAxiosRequestConfig`, allowing consumers to define additional properties
 *                    used within the interceptors or request configs.
 *
 * @param {object} options - Configuration options for the client.
 * @param {string} [options.baseURL] - The base URL for all HTTP requests. Defaults to `undefined`.
 * @param {Record<string, string>} [options.headers] - Default headers applied to every request. Defaults to `{}`.
 * @param {(err: AxiosError) => void} [options.onError] - Optional error handler called when a request or response
 *                                                       interceptor fails. If omitted, Axios will throw errors normally.
 * @param {RequestInterceptor[]} [options.requestInterceptors] - An array of async functions (request interceptors).
 *                                                               Each function receives and returns an Axios request config.
 *                                                               Defaults to an empty array.
 * @param {ResponseInterceptor[]} [options.responseInterceptors] - An array of async functions (response interceptors).
 *                                                                 Each function receives and returns an Axios response.
 *                                                                 If none are provided, a default interceptor simply
 *                                                                 returns the original response payload.
 *
 * @returns {object} A client object containing shorthand HTTP methods: `get`, `post`, `patch`, `put`, and `delete`.
 *                   Each method returns a promise that resolves with the Axios response of the given type.
 *
 * @example
 * import createAxiosClient from "./createAxiosClient";
 * import { z } from "zod";
 *
 * // Example of a Zod schema you might use to parse responses
 * const UserSchema = z.object({
 *   id: z.number(),
 *   name: z.string(),
 * });
 *
 * // Create a client with a baseURL, custom headers, and interceptors
 * const client = createAxiosClient({
 *   baseURL: "https://api.example.com",
 *   headers: { Authorization: "Bearer token" },
 *   onError: (err) => {
 *     console.error("API error:", err.message);
 *   },
 *   requestInterceptors: [
 *     async (config) => {
 *       // e.g., attach additional headers or parameters
 *       config.headers["X-Custom-Header"] = "MyValue";
 *       return config;
 *     },
 *   ],
 *   responseInterceptors: [
 *     async (response) => {
 *       // e.g., transform the response data or validate it with a schema
 *       UserSchema.parse(response.data);
 *       return response;
 *     },
 *   ],
 * });
 *
 * // Use the client to fetch data
 * const fetchUser = async (id: number) => {
 *   const response = await client.get(`/users/${id}`);
 *   return response.data; // typed as unknown by default, or validated if using a schema
 * };
 */
const createAxiosClient = function (_ref) {
  let {
    baseURL,
    headers,
    onError,
    requestInterceptors = [],
    responseInterceptors = []
  } = _ref;
  const request = _axios.default.create({
    baseURL,
    headers
  });
  request.interceptors.request.use(createInterceptorStack(requestInterceptors), onError);
  request.interceptors.response.use(createInterceptorStack([
  // If no response interceptors are provided then at least
  // one response interceptor must return the original response payload
  async function (res) {
    return res;
  }, ...responseInterceptors]), onError);
  const makeHeaders = function (args, data) {
    return {
      ...args,
      ...(data !== null && data !== void 0 && data.responseType ? {
        responseType: data.responseType
      } : {}),
      ...(args !== null && args !== void 0 && args.headers ? {
        headers: args.headers
      } : {})
    };
  };
  return {
    get: function (path, config) {
      return request.get(path, makeHeaders(config));
    },
    post: function (path, data, config) {
      return request.post(path, data, makeHeaders(config, data));
    },
    patch: function (path, data, config) {
      return request.patch(path, data, makeHeaders(config, data));
    },
    put: function (path, data, config) {
      return request.put(path, data, makeHeaders(config, data));
    },
    delete: function (path, config) {
      return request.delete(path, makeHeaders(config));
    }
  };
};
var _default = exports.default = createAxiosClient;